import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import MapAndStreetView from 'dashboard/components/ReportResult/MapAndStreetView';
import { ReportError } from 'dashboard/components/ReportResult/ReportError';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import Tooltip from 'dashboard/components/Tooltip';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { ReportAddressLookup } from 'dashboard/models/report/address-lookup';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';

interface AddressLookupProps {
  report: ReportAddressLookup;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

export const AddressLookup = ({ report, isRedacted, multiCardFormat }: AddressLookupProps) =>
  report.attributes['error-message'] ? (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <ReportError>
        <Paragraph color='tertiary'>{report.attributes['error-message']}</Paragraph>
      </ReportError>
    </ReportResultLayout>
  ) : (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <AddressDetail report={report} isRedacted={isRedacted} />
      {report.attributes['display-building-details'] && (
        <BuildingDetails report={report} isRedacted={isRedacted} />
      )}
      <DeliveryDetails report={report} isRedacted={isRedacted} />
      <MailingDetails report={report} isRedacted={isRedacted} />
      <TimezoneDetails report={report} isRedacted={isRedacted} />
    </ReportResultLayout>
  );

interface AddressDetailProps {
  report: ReportAddressLookup;
  isRedacted: boolean;
}

const AddressDetail = (props: AddressDetailProps) => {
  const {
    report: {
      attributes: {
        'resolved-address-street-1': resolvedAddressStreet1,
        'resolved-address-street-2': resolvedAddressStreet2,
        'resolved-address-city': resolvedAddressCity,
        'resolved-address-subdivision': resolvedAddressSubdivision,
        'resolved-address-postal-code': resolvedAddressPostalCode,
        'metadata-record-type': metadataRecordType,
        'metadata-zip-type': metadataZipType,
        'metadata-county-fips': metadataCountyFips,
        'metadata-county-name': metadataCountyName,
        'metadata-congressional-district': metadataCongressionalDistrict,
        'metadata-building-default-indicator': metadataBuildingDefaultIndicator,
        'metadata-latitude': metadataLatitude,
        'metadata-longitude': metadataLongitude,
        'metadata-precision': metadataPrecision,
      },
    },
    isRedacted,
  } = props;

  return (
    <>
      <Card.Title>Address Details</Card.Title>
      <MapAndStreetView
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lat={metadataLatitude}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lng={metadataLongitude}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        addressStreet1={resolvedAddressStreet1}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        addressStreet2={resolvedAddressStreet2}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        addressCity={resolvedAddressCity}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        addressSubdivision={resolvedAddressSubdivision}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        addressPostalCode={resolvedAddressPostalCode}
        data-chromatic='ignore'
      />
      <AttributeGrid>
        <AttributeItem
          title='Resolved/Corrected Address'
          value={
            <p>
              {resolvedAddressStreet1}
              <br />
              {resolvedAddressStreet2 && (
                <>
                  {resolvedAddressStreet2} <br />
                </>
              )}
              {resolvedAddressCity}, {resolvedAddressSubdivision}, {resolvedAddressPostalCode}
            </p>
          }
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__resolved-address'
        />
        <AttributeItem
          title='Record Type'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={displayRecordType(metadataRecordType)}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-record-type'
        />
        <AttributeItem
          title='Zip Type'
          value={metadataZipType}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-zip-type'
        />
        <AttributeItem
          title='County Name'
          value={metadataCountyName}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-county-name'
        />
        <AttributeItem
          title='County FIPS'
          value={metadataCountyFips}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-county-fips'
        />
        <AttributeItem
          title='Building Default Indicator'
          value={metadataBuildingDefaultIndicator}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-building-default-indicator'
        />
        <AttributeItem
          title='Latitude'
          value={metadataLatitude}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-latitude'
        />
        <AttributeItem
          title='Longitude'
          value={metadataLongitude}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-longitude'
        />
        <AttributeItem
          title='Precision'
          value={metadataPrecision}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-precision'
        />
        <AttributeItem
          title='Congressional District'
          value={metadataCongressionalDistrict}
          placeholder='N/A'
          colCount={3}
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-congressional-district'
        />
      </AttributeGrid>
    </>
  );
};

const DeliveryDetails = (props: { report: ReportAddressLookup; isRedacted: boolean }) => {
  const {
    report: {
      attributes: {
        'metadata-residential-delivery-indicator': metadataResidentialDeliveryIndicator,
        'metadata-carrier-route': metadataCarrierRoute,
        'metadata-carrier-route-type': metadataCarrierRouteType,
        'analysis-dpv-match-code-decoded': analysisDpvMatchCode,
        'analysis-dpv-footnotes-decoded': analysisDpvFootnotes,
        'analysis-dpv-cmra': analysisDpvCmra,
        'analysis-dpv-vacant': analysisDpvVacant,
        'analysis-active': analysisActive,
        'analysis-footnotes-decoded': analysisFootnotes,
        deliverable,
      },
    },
    isRedacted,
  } = props;

  return (
    <>
      <Card.Title>Delivery Details</Card.Title>
      <AttributeGrid>
        <AttributeItem
          title='Residential Delivery Indicator'
          value={metadataResidentialDeliveryIndicator}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-residential-delivery-indicator'
        />
        <AttributeItem
          title='Carrier Route'
          value={metadataCarrierRoute}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-carrier-route'
        />
        <AttributeItem
          title='Carrier Route Type'
          value={metadataCarrierRouteType}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-carrier-route-type'
        />
        <AttributeItem
          title='DPV Match Code'
          value={analysisDpvMatchCode}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-dpv-match-code'
        />
        <AttributeItem
          title='DPV Footnotes'
          value={
            <ul>
              {analysisDpvFootnotes?.map((dpvFootnote) => {
                return <li key={dpvFootnote}>{dpvFootnote}</li>;
              })}
            </ul>
          }
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-dpv-footnotes'
        />
        <AttributeItem
          title='DPV CMRA (PMB)'
          value={analysisDpvCmra}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-dpv-cmra'
        />
        <AttributeItem
          title='DPV Vacant'
          value={analysisDpvVacant}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-dpv-vacant'
        />
        <AttributeItem
          title='Active'
          value={analysisActive}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-active'
        />
        <AttributeItem
          title='Is Deliverable'
          value={deliverable ? 'Y' : 'N'}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-deliverable'
        />
        <AttributeItem
          title='Footnotes'
          value={
            <ul>
              {analysisFootnotes?.map((footnote) => {
                return <li key={footnote}>{footnote}</li>;
              })}
            </ul>
          }
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-footnotes'
        />
      </AttributeGrid>
    </>
  );
};

const MailingDetails = (props: { report: ReportAddressLookup; isRedacted: boolean }) => {
  const {
    isRedacted,
    report: {
      attributes: {
        'analysis-lacs-link-code-decoded': analysisLacsLinkCode,
        'analysis-lacs-link-indicator-decoded': analysisLacsLinkIndicator,
        'metadata-ews-match': metadataEwsMatch,
        'analysis-suite-link-match': analysisSuiteLinkMatch,
      },
    },
  } = props;
  return (
    <>
      <Card.Title>Mailing Details</Card.Title>
      <AttributeGrid>
        <AttributeItem
          title='LACSLink Code'
          value={analysisLacsLinkCode}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-lacs-link-code'
        />
        <AttributeItem
          title='LACSLink Indicator'
          value={analysisLacsLinkIndicator}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-lacs-link-indicator'
        />
        <AttributeItem
          title='SuiteLink Match '
          value={analysisSuiteLinkMatch}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__analysis-suite-link-match'
        />
        <AttributeItem
          title='Early Warning System Flag'
          value={metadataEwsMatch}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-ews-match'
        />
      </AttributeGrid>
    </>
  );
};

const TimezoneDetails = (props: { report: ReportAddressLookup; isRedacted: boolean }) => {
  const {
    report: {
      attributes: {
        'metadata-time-zone': metadataTimeZone,
        'metadata-utc-offset': metadataUtcOffset,
        'metadata-obeys-daylight-savings': metadataObeysDaylightSavings,
      },
    },
    isRedacted,
  } = props;

  return (
    <>
      <Card.Title>Timezone Details</Card.Title>
      <AttributeGrid>
        <AttributeItem
          title='Time Zone'
          value={metadataTimeZone}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-time-zone'
        />
        <AttributeItem
          title='UTC Offset'
          value={metadataUtcOffset}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-utc-offset'
        />
        <AttributeItem
          title='Observes Daylight Savings Time'
          value={metadataObeysDaylightSavings ? 'Yes' : 'No'}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-obeys-daylight-savings'
        />
      </AttributeGrid>
    </>
  );
};

const BuildingDetails = (props: { report: ReportAddressLookup; isRedacted: boolean }) => {
  const {
    report: {
      attributes: {
        'building-details': buildingDetails,
        'streetview-image-1-url': streetviewImage1Url,
        'streetview-image-2-url': streetviewImage2Url,
        'streetview-image-1-date': streetviewImage1Date,
        'streetview-image-2-date': streetviewImage2Date,
        'satellite-image-1-url': satelliteImage1Url,
        'satellite-image-2-url': satelliteImage2Url,
      },
    },
    isRedacted,
  } = props;

  const analysis = buildingDetails?.['streetview-succeeded']
    ? buildingDetails?.['streetview-analysis']
    : buildingDetails?.['satellite-succeeded']
      ? buildingDetails?.['satellite-analysis']
      : {};

  return (
    <>
      <Card.Title>
        Street View Details
        <Tooltip
          trigger='hover'
          tooltip={
            <Paragraph color='primary' size='md'>
              AI extracted information about the address from Google Street View and Google
              Satellite images.
            </Paragraph>
          }
          hideArrow={true}
          placement='right'
        >
          <CircleInfoIcon />
        </Tooltip>
      </Card.Title>

      <AttributeGrid>
        {buildingDetails?.['streetview-succeeded'] ? (
          <>
            <div>
              <AsyncImage
                value={[{ url: streetviewImage1Url || '' }]}
                width='275px'
                height='275px'
              />
              Date Captured: {streetviewImage1Date}
            </div>
            <div>
              <AsyncImage
                value={[{ url: streetviewImage2Url || '' }]}
                width='275px'
                height='275px'
              />
              Date Captured: {streetviewImage2Date}
            </div>
            <div>
              <AsyncImage
                value={[{ url: satelliteImage1Url || '' }]}
                width='275px'
                height='275px'
              />
              Date Captured: N/A
            </div>
          </>
        ) : buildingDetails?.['satellite-succeeded'] ? (
          <>
            <AsyncImage value={[{ url: satelliteImage1Url || '' }]} width='275px' height='275px' />
            <AsyncImage value={[{ url: satelliteImage2Url || '' }]} width='275px' height='275px' />
            <div></div>
          </>
        ) : null}
      </AttributeGrid>
      <h4>Building Type</h4>

      <AttributeGrid>
        <AttributeItem
          title='Result'
          value={startCase(analysis?.['building-classification'] || '')}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-building-type'
        />
        <AttributeItem
          title='Confidence'
          value={startCase(analysis?.['confidence-level'] || '')}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-confidence'
        />
      </AttributeGrid>

      <AttributeItem
        title='Analysis'
        value={analysis?.['building-classification-analysis']}
        placeholder='N/A'
        isRedacted={isRedacted}
        dataTest='report-address-lookup__metadata-building-classification-analysis'
      />

      <h4>Other</h4>
      <AttributeGrid>
        <AttributeItem
          title='Extracted Business Names'
          value={analysis?.['business-names']}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-business-names'
        />
        <AttributeItem
          title='Description'
          value={analysis?.['description']}
          placeholder='N/A'
          isRedacted={isRedacted}
          dataTest='report-address-lookup__metadata-description'
        />
      </AttributeGrid>
    </>
  );
};

const displayRecordType = (type: string) => {
  switch (type) {
    case 'F':
      return 'Firm';
    case 'G':
      return 'General Delivery';
    case 'H':
      return 'High-rise';
    case 'P':
      return 'Post-office box section';
    case 'R':
      return 'Rural route or Highway Contract';
    case 'S':
      return 'Street address';
  }
};
